import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';
import Button from '../shared/Button';
import ConfirmModal from '../shared/ConfirmModal';
import supportTicketService from '../../services/supportTicketService';
import '../SupportPage.css';

// THE Support button — on the main screens of every product (CLAUDE.md "rich-context ticket"). The user says what is
// wrong; the screen supplies what it was showing, so the ticket can be fixed without a call back:
//
//   context   an object, or a function returning one, of what the screen shows — product, screen, entity ids
//             (hotelId / accountId / partnerId / syncId / vault, porter or pipeline id), the record, the filters in
//             effect, the failing request and its ids. It goes into the support team's copy of the ticket only. The
//             ticket lands in the queue of the hotel / chain / partner it names — checked on the server, never trusted.
//   submit    optional adapter, for a screen with richer evidence of its own to attach on the server (the Message
//             Log's record ticket): ({ subject, description, priority }) => Promise<{ ticketId }>. Default: the
//             shared POST /support/tickets with the context.
//   label     button text (default "Support"); `variant`, `className` pass through to the button.
//   onRaised  called with the new ticket id once it is raised (a ticket list beside the button reloads).
//
// After raising, the dialog links to the ticket in hub (/support/:id).

function SupportButton({ context, submit, label = 'Support', variant = 'secondary', className, defaultSubject = '', onRaised }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ subject: '', description: '', priority: 'medium' });
    const [state, setState] = useState({ busy: false, error: null, ticketId: null });

    const start = () => {
        setForm({ subject: defaultSubject, description: '', priority: 'medium' });
        setState({ busy: false, error: null, ticketId: null });
        setOpen(true);
    };

    const send = async (e) => {
        e.preventDefault();
        if (!form.subject.trim() || !form.description.trim()) { setState((s) => ({ ...s, error: 'Say what the problem is, and what you expected.' })); return; }
        setState({ busy: true, error: null, ticketId: null });
        try {
            const ctx = { ...(typeof context === 'function' ? context() : context || {}), pageUrl: window.location.href };
            const ticketId = submit
                ? (await submit({ subject: form.subject.trim(), description: form.description.trim(), priority: form.priority })).ticketId
                : (await supportTicketService.createTicket({ subject: form.subject.trim(), description: form.description.trim(),
                    priority: form.priority, type: 'issue', context: ctx })).data?.ticket?.id;
            setState({ busy: false, error: null, ticketId });
            if (onRaised) onRaised(ticketId);
        } catch (err) {
            setState({ busy: false, ticketId: null,
                error: err?.response?.data?.error?.message || err?.response?.data?.message || err.message || 'The ticket could not be raised' });
        }
    };

    return (
        <>
            <Button variant={variant} className={className} onClick={start}><LifeBuoy className="icon" /> {label}</Button>
            {open && (
                <ConfirmModal wide title="Contact support" onCancel={() => setOpen(false)} closeOnBackdrop={false}
                    hideConfirm={!!state.ticketId} cancelLabel={state.ticketId ? 'Close' : 'Cancel'}
                    confirmLabel={state.busy ? 'Sending…' : 'Raise ticket'} confirmDisabled={state.busy} formId="support-button-form"
                    message={state.ticketId ? null : 'Tell us what went wrong. What you were looking at is attached for the support team automatically.'}>
                    {state.ticketId ? (
                        <p className="support-success">
                            Ticket #{state.ticketId} raised. We will email you when its status changes —{' '}
                            <Link to={`/support/${state.ticketId}`} onClick={() => setOpen(false)}>open the ticket</Link>.
                        </p>
                    ) : (
                        <form id="support-button-form" onSubmit={send}>
                            <div className="support-form-group">
                                <label>Subject</label>
                                <input type="text" maxLength={255} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
                            </div>
                            <div className="support-form-group">
                                <label>What happened, and what did you expect?</label>
                                <textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
                            </div>
                            <div className="support-form-group">
                                <label>Priority</label>
                                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                                </select>
                            </div>
                            {state.error && <div className="support-form-error">{state.error}</div>}
                        </form>
                    )}
                </ConfirmModal>
            )}
        </>
    );
}

export default SupportButton;
